import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table, Button, UncontrolledTooltip } from "reactstrap";
import {
  Loading, WarningAlert, PaginationControls, ItemsPerPageSelector, SortableHeaderCell, EntityRow, ConfirmationModal,
  NotificationToast, SimpleFormModal, SearchBar, InformationalModal, SelectableListModal, ActionDropdown
} from "../components";
import { runWithNotification } from "../utils/runWithNotification";
import useSortableData from "../hooks/useSortableData";
import { useEntity } from "../hooks/useEntity";
import { sitesApi } from "../api/entitiesApi";
import { edgeVersionsApi } from "../api/entitiesApi";
import { updateDevicesApi } from "../api/actionsApi";
import { cronApi } from "../api/cronApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { siteFields, siteDefaultValues } from "../utils/entityFormFields";
import { Link } from "react-router-dom";

import "../App.css";
import { handleSiteSteeringFieldChange } from "../utils/formFieldCouplingHandler";

const SITE_FIELD_KEYS = [
  "customer_id", "display_name", "id", "postal_address",
  "latitude", "longitude", "timezone", "active_steering_mode", "preferred_steering_mode"
];

const renderSiteField = (field, value) => {
  if (field === "customer_id") {
    return <Link to={`/customerDetails/${value}`}>{value.substring(0, 4)}</Link>;
  }
  return value;
};

export function SitesComponent() {
  const { data: sites, isLoading, error, setData: setSites } = useEntity(sitesApi);
  const { items: sortedSites, requestSort, sortConfig } = useSortableData(sites, { key: '', direction: '' });
  const [steeringPrefs, setSteeringPrefs] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [sitesPerPage, setSitesPerPage] = useState(50);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [deleteData, setDeleteData] = useState({ open: false, siteId: null });
  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [pendingUpdateSite, setPendingUpdateSite] = useState(null);
  const [pendingUpdateVersion, setPendingUpdateVersion] = useState(null);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  const totalPages = Math.ceil((sites?.length || 0) / sitesPerPage);
  const indexOfLastSite = currentPage * sitesPerPage;
  const indexOfFirstSite = indexOfLastSite - sitesPerPage;
  const itemsPerPage = [50, 100];

  const filteredSites = sortedSites.filter(site => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return SITE_FIELD_KEYS.some((key) => site[key]?.toString().toLowerCase().includes(lowerTerm));
  });

  const currentSites = filteredSites.slice(indexOfFirstSite, indexOfLastSite);

  function toggleDropdown() { setDropdownOpen(prevState => !prevState); }
  function handlePageChange(pageNumber) { setCurrentPage(pageNumber); }
  function handleDropdownChange(number) { setSitesPerPage(number); setCurrentPage(1); }
  function resetUpdateState() {
    setShowUpdateConfirm(false);
    setPendingUpdateVersion(null);
    setPendingUpdateSite(null);
  }
  function showNotification(title, message) {
    setNotification({ visible: true, title, message });
    setTimeout(() => setNotification({ visible: false, title: "", message: "" }), 2000);
  }
  function showError(title, message) {
    setErrorMessage({ open: true, title, message });
  }

  async function handleEdit(site) {
    try {
      const prefs = await sitesApi.listSteeringPrefs(site.id);
      setSteeringPrefs(prefs);
      setCurrentForm({ mode: "edit", data: site });
    } catch (err) {
      setCurrentForm(null);
    }
  }

  function handleAdd() { setCurrentForm({ mode: "add", data: { customer_id: "" } }); }
  function handleCancelFormModal() { setCurrentForm(null); }
  function dismissInformationalModal() { setErrorMessage({ open: false, title: "", message: "" }); }

  async function handleSaveForm(formData) {
    try {
      if (currentForm.mode === "edit") {
        await sitesApi.updateEntity(formData.id, formData);
        setSites(prev => prev.map(site => site.id === formData.id ? formData : site));
        showNotification("Site Edited", "1 Site has been edited");
      } else {
        const newSite = await sitesApi.addEntity(formData);
        setSites(prev => [...prev, newSite]);
        showNotification("Site Added", "1 Site has been added");
      }
    } catch (err) {
      showError(currentForm.mode === "edit" ? "Error Updating Site" : "Error Adding Site", err.message);
    } finally {
      setCurrentForm(null);
    }
  }

  function openDeleteModal(site) { setDeleteData({ open: true, siteId: site.id }); }
  function cancelDelete() { setDeleteData({ open: false, siteId: null }); }

  async function confirmDelete() {
    try {
      await sitesApi.deleteEntity(deleteData.siteId);
      setSites(prev => prev.filter(site => site.id !== deleteData.siteId));
      showNotification("Site Deleted", "1 Site has been deleted");
    } catch (err) {
      showError("Error Deleting Site", err.message);
    } finally {
      cancelDelete();
    }
  }

  async function handleUpdateClick(site) {
    try {
      const response = await edgeVersionsApi.getData();
      if (!response || response.length === 0) {
        throw new Error();
      }
      response.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
      setAvailableVersions(response);
      setPendingUpdateSite(site);
      setIsUpdateModalOpen(true);
    } catch {
      showError("Error", "Failed to load versions.");
    }
  }

  async function confirmSiteUpdate(site, version) {
    try {
      await updateDevicesApi.post(version.id, site.id);
      showNotification("Site Updated", `Site "${site.id}" updated to version ${version.version}.`);
    } catch {
      showError("Update Failed", `Something went wrong while trying to update site ${site.id}.`);
    }
  }

  if (isLoading) return <Loading />;

  return (
    <>
      {sites.length === 0 ? (
        <div className="d-flex flex-column align-items-center">
          <WarningAlert message={error || "Ooops! No sites found"} />
          <br />
          <div className="d-flex justify-content-center">

            <Button color="primary" onClick={handleAdd} aria-label={error ? "Create First Site" : "Create Site"}>Create</Button>
          </div>
        </div>
      ) : (
        <div className="mb-5">
          <div className="d-flex justify-content-end align-items-center mb-2">
            <Button color="transparent" onClick={handleAdd} aria-label="Add Site">
              <FontAwesomeIcon icon="plus" />
            </Button>
            <ActionDropdown
              actions={[
                {
                  label: "Trigger Solar Forecast",
                  onClick: () => runWithNotification({
                    fn: cronApi.triggerSolarForecast,
                    successMessage: "Solar forecast triggered successfully.",
                    errorMessage: "Failed to trigger solar forecast.",
                    setNotification: setNotification,
                    setError: setErrorMessage
                  })
                },
                {
                  label: "Trigger Carbon Intensity Fetch",
                  onClick: () => runWithNotification({
                    fn: cronApi.triggerCarbonIntensityData,
                    successMessage: "Carbon Intensity triggered successfully.",
                    errorMessage: "Failed to trigger carbon intensity.",
                    setNotification: setNotification,
                    setError: setErrorMessage
                  })
                },
                {
                  label: "Trigger Load Forecast",
                  onClick: () => runWithNotification({
                    fn: cronApi.triggerLoadForecast,
                    successMessage: "Load forecast triggered successfully.",
                    errorMessage: "Failed to trigger load forecast.",
                    setNotification: setNotification,
                    setError: setErrorMessage
                  })
                },
                {
                  label: "Trigger Schedule Generation",
                  onClick: () => runWithNotification({
                    fn: cronApi.triggerScheduleGeneration,
                    successMessage: "Schedule generation triggered successfully.",
                    errorMessage: "Failed to trigger schedule generation.",
                    setNotification: setNotification,
                    setError: setErrorMessage
                  })
                },
                {
                  label: "Trigger KPIs calculation",
                  onClick: () => runWithNotification({
                    fn: cronApi.triggerKPICalculation,
                    successMessage: "Kpi calculation triggered successfully.",
                    errorMessage: "Failed to trigger kpi calculation.",
                    setNotification: setNotification,
                    setError: setErrorMessage
                  })
                }
              ]}
            />
            <div style={{ padding: "6px 12px" }}>
              <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>
          </div>
          {filteredSites.length > 0 ? (
            <>
              <Table bordered hover responsive size="sm" striped>
                <thead>
                  <tr>
                    {SITE_FIELD_KEYS.map((key) => (
                      <SortableHeaderCell key={key} columnKey={key} label={key} currentSort={sortConfig} onSort={requestSort} />
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSites.map(site => (
                    <EntityRow
                      key={site.id}
                      entity={site}
                      fields={SITE_FIELD_KEYS}
                      onEdit={handleEdit}
                      onDelete={openDeleteModal}
                      isSelected={
                        (currentForm && currentForm.mode === "edit" && site.id === currentForm.data.id) ||
                        site.id === deleteData.siteId
                      }
                      renderField={renderSiteField}
                      extraActions={(_site, idPrefix) => (
                        <>
                          <Button
                            id={`${idPrefix}-update`}
                            size="sm"
                            color="link"
                            aria-label="Update site"
                            onClick={() => handleUpdateClick(site)}
                          >
                            <FontAwesomeIcon
                              icon="code-compare"
                              style={{ fontSize: "1.2em", color: "#17a2b8" }}
                            />
                          </Button>
                          <UncontrolledTooltip
                            target={`${idPrefix}-update`}
                            placement="top"
                          >
                            Update edge version
                          </UncontrolledTooltip>
                        </>
                      )}
                    />
                  ))}
                </tbody>
              </Table>

              <div className="d-flex justify-content-between align-items-center">
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <ItemsPerPageSelector items={itemsPerPage} currentSelection={sitesPerPage} onSelect={handleDropdownChange} isOpen={dropdownOpen} toggle={toggleDropdown} />
              </div>
            </>
          ) : (
            <WarningAlert message="Ooops! No sites found" />
          )}
        </div>
      )}

      <InformationalModal isOpen={errorMessage.open} title={errorMessage.title} message={errorMessage.message} onCancel={dismissInformationalModal} />

      <ConfirmationModal isOpen={deleteData.open} title="Delete Site?" message="Are you sure you want to delete this site?" onConfirm={confirmDelete} onCancel={cancelDelete} confirmLabel="OK" cancelLabel="Cancel" />

      <SimpleFormModal
        isOpen={!!currentForm}
        title={currentForm?.mode === "edit" ? "Edit Site" : "Add Site"}
        initialData={currentForm?.data}
        fields={
          currentForm?.mode === "edit"
            ? [
              ...siteFields(),
              {
                name: "active_steering_mode", label: "ActiveSteeringMode", type: "select",
                options: steeringPrefs.map((p) => ({
                  value: p.id,
                  label: p.preference_type
                }))
              },
              {
                name: "preferred_steering_mode", label: "PreferredSteeringMode", type: "select",
                options: steeringPrefs
                  .filter((p) => p.preference_type !== "Idle")
                  .map((p) => ({
                    value: p.id,
                    label: p.preference_type
                  }))
              },
            ]
            : siteFields()
        }
        onSave={handleSaveForm}
        onCancel={handleCancelFormModal}
        defaultValues={siteDefaultValues}
        onFieldChange={handleSiteSteeringFieldChange(steeringPrefs)}
      />

      <ConfirmationModal
        isOpen={showUpdateConfirm}
        title="Confirm Update"
        message={pendingUpdateSite && pendingUpdateVersion ? `Update site "${pendingUpdateSite.id}" to version "${pendingUpdateVersion.version}"?` : ""}
        onConfirm={() => { confirmSiteUpdate(pendingUpdateSite, pendingUpdateVersion); resetUpdateState(); }}
        onCancel={resetUpdateState}
        confirmLabel="Yes, Update"
        cancelLabel="Cancel"
      />

      <SelectableListModal
        isOpen={isUpdateModalOpen}
        title="Update Site Version"
        items={availableVersions}
        helpMessage="* This will update the selected site to the chosen version."
        getItemId={(v) => v.id}
        renderItem={(v) => (
          <>
            <div className="fw-semibold">{v.version}</div>
            <div className="small text-muted">Uploaded: {new Date(v.uploaded_at).toLocaleString()}</div>
          </>
        )}
        onCancel={() => setIsUpdateModalOpen(false)}
        onConfirm={(selectedItem) => {
          setPendingUpdateVersion(selectedItem);
          setIsUpdateModalOpen(false);
          setShowUpdateConfirm(true);
        }}
      />

      {notification.visible && (
        <NotificationToast icon="exclamation-triangle" title={notification.title} message={notification.message} fade={true} />
      )}
    </>
  );
}

export default withAuthenticationRequired(SitesComponent, { onRedirecting: () => <Loading /> });